import { Button, PromoLabel } from '@velocity/ui';
import React, { useRef } from 'react';
import { Type } from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import CarImage from '../../../common/icons/hidden-car.svg?url';
import shapeDesktop from '../../../common/sub-header/assets/subheader-shape-desktop.svg?url';
import { useSelectApplication } from './use-select-application';

/*
 * Lease application card (Figma: Instant Lease - B2C, "Desktop" 1964:2058).
 *
 *   card        380 wide, 1px neutral-300 border, 8px radius, white
 *   band        126px brand-support2-400 with the Ayvens shape (56px / -24px)
 *   labels      8px inset: customer type pill (left), application state (right)
 *   car image   147px high, starts 56px below the card top (overlaps the band)
 *   body        16px side padding, 24px bottom padding, CTA pinned to the bottom
 *
 * Typography uses the global token classes (src/styles/typography.css); the
 * colour overrides need the important modifier because the global `p` rules are
 * unlayered.
 */
const textColor = 'text-(--vel-color-secondary-400,#0f3549)!';
const mutedColor = 'text-(--vel-color-neutral-600,#757777)!';
const dateColor = 'text-(--vel-color-primary-400,#008282)!';

const priceFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const listPriceFormatter = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 0,
});

export interface CarSelectionProps {
  uuid: string;
  img?: string;
  /** Vehicle headline, e.g. "Opel Corsa" (brand + model) */
  carName?: string;
  /** Vehicle variant line, e.g. "Corsa 1.2 Start/Stop O" */
  variantName?: string;
  /** B2C = Privat / inkl. MwSt., B2B = Gewerblich / exkl. MwSt. */
  type?: Type;
  /** Monthly rate (gross for B2C, net for B2B) */
  rate?: number | string;
  /** Vehicle list price */
  listPrice?: number | string;
  /** Contract duration in months */
  duration?: number;
  /** Yearly mileage in km */
  mileage?: number;
  /** Offer date, already formatted (DD.MM.YYYY) */
  appLeaseDate?: string | Date | number | null;
  /** Offer time, already formatted (HH:mm) */
  time?: string;
  /**
   * Marks the most recently created application ("Neuer Antrag"). The other
   * applications are shown as the current one ("Aktueller Antrag").
   */
  isNewApplication?: boolean;
}

const FLY_DURATION_MS = 700;

/*
 * Flies a copy of the car image into the sub-header band so the user sees the
 * chosen car "move" into the header. Resolves when done (or immediately when
 * animations are unavailable / reduced motion is requested).
 */
const flyImageToSubheader = async (image: HTMLImageElement | null) => {
  const target = document.querySelector<HTMLElement>(
    '[data-testid="subheader"]',
  );
  if (
    !image ||
    !target ||
    typeof image.animate !== 'function' ||
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const from = image.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const scale = Math.min(1, to.height / from.height);
  const endLeft = to.right - from.width * scale - 16;
  const endTop = to.top + (to.height - from.height * scale) / 2;

  const clone = image.cloneNode(true) as HTMLImageElement;
  clone.removeAttribute('data-testid');
  Object.assign(clone.style, {
    position: 'fixed',
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: '0',
    zIndex: '1000',
    pointerEvents: 'none',
    transformOrigin: 'top left',
  });
  document.body.appendChild(clone);
  image.style.visibility = 'hidden';

  try {
    await clone.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        {
          transform: `translate(${endLeft - from.left}px, ${endTop - from.top}px) scale(${scale})`,
          opacity: 0.9,
        },
      ],
      {
        duration: FLY_DURATION_MS,
        easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
        fill: 'forwards',
      },
    ).finished;
  } catch {
    // animation cancelled, continue with navigation
  } finally {
    clone.remove();
    image.style.visibility = '';
  }
};

const toNumber = (value?: number | string): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const CarSelection: React.FC<CarSelectionProps> = ({
  img,
  uuid,
  carName = '',
  variantName = '',
  type,
  rate,
  listPrice,
  duration,
  mileage,
  appLeaseDate = '',
  time = '',
  isNewApplication = false,
}) => {
  const { selectApplication, isSelecting } = useSelectApplication();
  const imageRef = useRef<HTMLImageElement>(null);
  const [isAnimating, setIsAnimating] = React.useState(false);

  const handleSelect = async () => {
    setIsAnimating(true);
    await flyImageToSubheader(imageRef.current);
    setIsAnimating(false);
    selectApplication(uuid);
  };

  const formattedDate =
    appLeaseDate instanceof Date
      ? appLeaseDate.toLocaleDateString('de-DE')
      : appLeaseDate;

  const isPrivate = type === Type.B2C;
  const vatLabel = getText(
    isPrivate ? 'car_selection_vat_incl' : 'car_selection_vat_excl',
  );

  const rateValue = toNumber(rate);
  const listPriceValue = toNumber(listPrice);

  const ctaLabel = getText(
    isNewApplication ? 'car_selection_cta_new' : 'car_selection_cta_continue',
  );

  return (
    <article
      className='flex h-full flex-col overflow-hidden rounded-lg border border-solid border-(--vel-color-neutral-300,#ddd) bg-white text-left'
      data-testid='car-selection'
    >
      {/* Teal band with the Ayvens shape and the labels */}
      <div className='relative h-[126px] shrink-0 overflow-hidden bg-(--vel-color-brand-support2-400,#73d2d2)'>
        <img
          src={shapeDesktop}
          alt=''
          aria-hidden='true'
          width={282.833}
          height={216.178}
          className='pointer-events-none absolute -top-6 left-14 m-0 block h-[216.178px] w-[282.833px] max-w-none select-none'
        />
        <div className='relative flex items-start justify-between gap-2 p-2'>
          {type && (
            <span
              className='text-tight-200-bold inline-flex h-6 items-center rounded-[20px] bg-(--vel-color-secondary-400,#0f3549) px-2 whitespace-nowrap text-white'
              data-testid='car-selection-type'
            >
              {getText(
                isPrivate ? 'car_selection_private' : 'car_selection_business',
              )}
            </span>
          )}
          <PromoLabel
            kind={isNewApplication ? 'primary' : 'brand-support3'}
            className={`ml-auto ${
              isNewApplication
                ? ''
                : 'bg-(--vel-color-brand-support3-400,#0073c8)!'
            }`.trim()}
            data-testid='car-selection-state'
          >
            {getText(
              isNewApplication
                ? 'car_selection_state_new'
                : 'car_selection_state_current',
            )}
          </PromoLabel>
        </div>
      </div>

      {/* Car image: starts 56px below the card top and overlaps the band */}
      <div className='relative -mt-[70px] flex h-[147px] shrink-0 items-center justify-center px-4'>
        <img
          src={img || CarImage}
          ref={imageRef}
          alt='Car'
          className='m-0 block h-full w-auto max-w-[240px] object-contain'
          data-testid='car-selection-image'
        />
      </div>

      <div className='flex flex-1 flex-col px-4 pt-6 pb-6'>
        <div className='flex flex-col'>
          <p
            className={`text-tight-100-bold m-0! ${dateColor}`}
            data-testid='car-selection-date'
          >
            {appLeaseDate
              ? `${getText('car_selection_offer_date')}: ${formattedDate}${
                  time ? ` | ${time}` : ''
                }`
              : getText('car_selection_no_application')}
          </p>
          <h2
            className={`headline-300 m-0! ${textColor}`}
            data-testid='car-selection-name'
          >
            {carName}
          </h2>
          {variantName && (
            <p className={`text-tight-200 m-0! ${textColor}`}>{variantName}</p>
          )}
        </div>

        <div className='mt-4 flex flex-col'>
          <p className={`text-tight-200-bold m-0! ${textColor}`}>
            {getText('car_selection_vehicle_details')}
          </p>

          {listPriceValue !== undefined && (
            <p
              className={`text-tight-100 m-0! mt-6 whitespace-nowrap ${mutedColor}`}
              data-testid='car-selection-list-price'
            >
              {getText('car_selection_list_price')}{' '}
              <span className={`text-tight-100-bold ${textColor}`}>
                € {listPriceFormatter.format(listPriceValue)}
              </span>{' '}
              {vatLabel}
            </p>
          )}

          {rateValue !== undefined && (
            <p
              className={`m-0! mt-1 flex items-end gap-1 whitespace-nowrap ${textColor}`}
              data-testid='car-selection-rate'
            >
              <span className='price-text-200'>
                € {priceFormatter.format(rateValue)}
              </span>{' '}
              <span className='text-tight-100 mb-1'>
                {getText('car_selection_monthly')} {vatLabel}
              </span>
            </p>
          )}

          {duration && mileage ? (
            <p
              className={`text-tight-100 m-0! mt-1 ${mutedColor}`}
              data-testid='car-selection-terms'
            >
              {getText('car_selection_based_on')} {duration}{' '}
              {getText('car_selection_months')} &amp;{' '}
              {Math.floor(mileage).toLocaleString('de-DE')}km
            </p>
          ) : null}
        </div>

        <div className='mt-auto pt-6'>
          <Button
            variant={isNewApplication ? 'primary-outlined' : 'primary-filled'}
            size='l'
            fullWidth
            data-testid={ctaLabel}
            disabled={isSelecting || isAnimating}
            onClick={handleSelect}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </article>
  );
};

export default CarSelection;
